import { Ros } from "../../../foxglove-client";
import { DisconnectedError } from "../../../foxglove-client/Impl";
import { FoxgloveControlSource } from "../../control_source/Foxglove";
import { Subscriber } from "../../control_source/base";
import { GoalStatusCode } from "..";
import {
  ActionCaller,
  ActionCallbacks,
  ActionHandle,
} from "../base";
import { randomUuidString, uuidBytesToString, uuidStringToBytes } from "../uuid";

export type FoxgloveActionOptions = { ros: Ros };

// get_result blocks server-side until the goal reaches a terminal state, so it must
// opt out of the default per-call service timeout.
const NO_TIMEOUT = { timeoutMs: 0 };

const TERMINAL_STATES = new Set<GoalStatusCode>([
  GoalStatusCode.STATUS_SUCCEEDED,
  GoalStatusCode.STATUS_ABORTED,
  GoalStatusCode.STATUS_CANCELED,
]);

// --- CDR message shapes (goal_id is unique_identifier_msgs/UUID: uint8[16]) ---
interface UuidMsg {
  uuid: number[] | Uint8Array;
}
interface StatusEntry {
  goal_info: { goal_id: UuidMsg };
  status: GoalStatusCode;
}
interface StatusArray {
  status_list: StatusEntry[];
}
interface FeedbackMsg {
  goal_id: UuidMsg;
  feedback: unknown;
}
interface SendGoalResponse {
  accepted: boolean;
}
interface GetResultResponse {
  status: GoalStatusCode;
  result?: unknown;
}

/** Return a shallow copy of `obj` without the given keys. */
function omit(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) if (!keys.includes(k)) out[k] = obj[k];
  return out;
}

function wrapHiddenError(actionName: string, err: unknown): Error {
  const msg = err instanceof Error ? err.message : String(err);
  if (/Timed out .* waiting for (service|topic)/.test(msg)) {
    return new Error(
      `${msg}. Action "${actionName}" is not visible: launch foxglove_bridge with ` +
      `include_hidden:=true so its /_action/ services and topics are advertised.`
    );
  }
  return err instanceof Error ? err : new Error(msg);
}

class FoxgloveGoalHandle implements ActionHandle {
  settled = false;
  constructor(
    readonly goalId: string,
    private readonly actionName: string,
    private readonly channel: ActionChannel,
    private readonly caller: FoxgloveActionCaller,
    private readonly cb: ActionCallbacks
  ) { }

  onStatus(status: GoalStatusCode) {
    this.cb.onStatus?.(status);
  }

  onFeedback(feedback: unknown) {
    this.cb.onFeedback?.(feedback);
  }

  settle(status: GoalStatusCode, result: unknown, error?: string) {
    if (this.settled) return;
    this.settled = true;
    this.channel.detach(this.goalId);
    if (error !== undefined) {
      this.cb.onFailed?.(error);
    } else if (status === GoalStatusCode.STATUS_SUCCEEDED) {
      this.cb.onResult?.(result, status);
    } else if (status === GoalStatusCode.STATUS_ABORTED) {
      this.cb.onFailed?.("aborted");
    } else if (status === GoalStatusCode.STATUS_CANCELED) {
      this.cb.onFailed?.("canceled");
    } else if (status === GoalStatusCode.STATUS_UNKNOWN) {
      this.cb.onFailed?.("result expired or unavailable");
    } else {
      this.cb.onResult?.(result, status);
    }
  }

  async cancel() {
    await this.caller.cancel(this.actionName, this.goalId);
  }

  stop() {
    this.settled = true;
    this.channel.detach(this.goalId);
  }
}

/**
 * One shared status/feedback subscription per action, fanning out to per-goal handles.
 * Multiple concurrent goals on the same action share this channel.
 */
class ActionChannel {
  private control: FoxgloveControlSource;
  private handles = new Map<string, FoxgloveGoalHandle>();
  private started = false;
  private statusHandler?: Subscriber;
  private feedbackHandler?: Subscriber;

  constructor(readonly actionName: string, ros: Ros) {
    this.control = FoxgloveControlSource.fromSocket(ros);
  }

  async ensureStarted() {
    if (this.started) return;
    this.started = true;

    this.statusHandler = (data: StatusArray) => {
      if (!data?.status_list) return;
      for (const entry of data.status_list) {
        const id = uuidBytesToString(entry.goal_info.goal_id.uuid);
        this.handles.get(id)?.onStatus(entry.status);
      }
    };
    this.feedbackHandler = (data: FeedbackMsg) => {
      const id = uuidBytesToString(data.goal_id.uuid);
      // The bridge inlines the feedback fields alongside goal_id (no `feedback`
      // wrapper); fall back to nested `feedback` if a bridge ever provides it.
      const feedback =
        data.feedback !== undefined
          ? data.feedback
          : omit(data as unknown as Record<string, unknown>, ["goal_id"]);
      this.handles.get(id)?.onFeedback(feedback);
    };

    // On reconnect these topics are re-subscribed automatically by the client.
    await this.control.subscribe(`${this.actionName}/_action/status`, this.statusHandler);
    await this.control.subscribe(`${this.actionName}/_action/feedback`, this.feedbackHandler);
  }

  attach(handle: FoxgloveGoalHandle) {
    this.handles.set(handle.goalId, handle);
  }

  detach(goalId: string) {
    this.handles.delete(goalId);
    if (this.handles.size === 0) this.stop();
  }

  liveHandles(): FoxgloveGoalHandle[] {
    const live: FoxgloveGoalHandle[] = [];
    this.handles.forEach((handle) => {
      if (!handle.settled) live.push(handle);
    });
    return live;
  }

  private stop() {
    if (!this.started) return;
    if (this.statusHandler)
      this.control.unsubscribe(`${this.actionName}/_action/status`, this.statusHandler);
    if (this.feedbackHandler)
      this.control.unsubscribe(`${this.actionName}/_action/feedback`, this.feedbackHandler);
    this.started = false;
  }
}

export class FoxgloveActionCaller extends ActionCaller {
  private ros: Ros;
  private channels = new Map<string, ActionChannel>();

  constructor(options: FoxgloveActionOptions) {
    super();
    this.ros = options.ros;
    // On reconnect, status/feedback resubscribe automatically; re-issue the
    // long-running get_result for any goal still being tracked.
    this.ros.on("connection", () => this.reissueResults());
  }

  async sendGoal<TGoal = unknown, TFeedback = unknown, TResult = unknown>(
    actionName: string,
    goal: TGoal,
    callbacks: ActionCallbacks<TFeedback, TResult> = {}
  ): Promise<ActionHandle> {
    const goalId = randomUuidString();
    const channel = this.channelFor(actionName);
    const handle = new FoxgloveGoalHandle(
      goalId,
      actionName,
      channel,
      this,
      callbacks as ActionCallbacks
    );
    try {
      await channel.ensureStarted();
      channel.attach(handle);
      // foxglove_bridge advertises the SendGoal request with the action Goal's
      // fields INLINED (no `goal` wrapper): `{ goal_id, <goal fields...> }`. CDR is
      // positional, so spread the goal fields at the top level — nesting them under
      // `goal` makes the writer emit defaults (empty) for every goal field.
      const resp = await this.call<SendGoalResponse>(`${actionName}/_action/send_goal`, {
        goal_id: { uuid: uuidStringToBytes(goalId) },
        ...(goal as Record<string, unknown>),
      });
      if (!resp.accepted) {
        channel.detach(goalId);
        const err = new Error(`Goal rejected by action server "${actionName}"`);
        callbacks.onFailed?.(err.message);
        throw err;
      }
    } catch (err) {
      channel.detach(goalId);
      throw wrapHiddenError(actionName, err);
    }
    this.awaitResult(actionName, handle);
    return handle;
  }

  async monitorGoal<TFeedback = unknown, TResult = unknown>(
    actionName: string,
    goalId: string,
    callbacks: ActionCallbacks<TFeedback, TResult> = {}
  ): Promise<ActionHandle> {
    const channel = this.channelFor(actionName);
    const handle = new FoxgloveGoalHandle(
      goalId,
      actionName,
      channel,
      this,
      callbacks as ActionCallbacks
    );
    try {
      await channel.ensureStarted();
      channel.attach(handle);
    } catch (err) {
      channel.detach(goalId);
      throw wrapHiddenError(actionName, err);
    }
    this.awaitResult(actionName, handle);
    return handle;
  }

  async cancel(actionName: string, goalId: string): Promise<void> {
    await this.call(`${actionName}/_action/cancel_goal`, {
      goal_info: {
        goal_id: { uuid: uuidStringToBytes(goalId) },
        stamp: { sec: 0, nanosec: 0 },
      },
    });
  }

  private channelFor(actionName: string): ActionChannel {
    let channel = this.channels.get(actionName);
    if (!channel) {
      channel = new ActionChannel(actionName, this.ros);
      this.channels.set(actionName, channel);
    }
    return channel;
  }

  /** Await the (long-running) get_result and settle the handle. */
  private awaitResult(actionName: string, handle: FoxgloveGoalHandle) {
    this.call<GetResultResponse>(
      `${actionName}/_action/get_result`,
      { goal_id: { uuid: uuidStringToBytes(handle.goalId) } },
      NO_TIMEOUT
    )
      .then((res) => {
        // GetResult response inlines the Result fields alongside `status` (no
        // `result` wrapper); fall back to nested `result` if a bridge provides it.
        const result =
          res.result !== undefined
            ? res.result
            : omit(res as unknown as Record<string, unknown>, ["status"]);
        handle.settle(res.status, result);
      })
      .catch((err) => {
        // Disconnect: leave the handle un-settled so reconnect can re-issue.
        if (err instanceof DisconnectedError) return;
        handle.settle(
          GoalStatusCode.STATUS_UNKNOWN,
          undefined,
          wrapHiddenError(actionName, err).message
        );
      });
  }

  private reissueResults() {
    this.channels.forEach((channel, actionName) => {
      for (const handle of channel.liveHandles()) {
        this.awaitResult(actionName, handle);
      }
    });
  }

  private async call<T>(service: string, request: unknown, opts?: { timeoutMs?: number }): Promise<T> {
    const impl = this.ros.rosImpl;
    if (!impl) {
      throw new Error(`Foxglove client is not connected (service "${service}")`);
    }
    return impl.sendServiceRequest<unknown, T>(service, request, opts);
  }
}
